import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Ticket, Clock, CheckCircle, Users, ArrowRight, TrendingUp, AlertCircle, Shield, RefreshCw, Timer } from 'lucide-react';
import api from '../../services/api';

const statusColors = {
  'Yeni': 'bg-blue-100 text-blue-700',
  'İşleme Alındı': 'bg-purple-100 text-purple-700',
  'Devam Ediyor': 'bg-amber-100 text-amber-700',
  'Beklemede': 'bg-gray-100 text-gray-600',
  'Logo\'ya İletildi': 'bg-indigo-100 text-indigo-700',
  'Müşteri Yanıtı Bekleniyor': 'bg-pink-100 text-pink-700',
  'Çözüldü': 'bg-emerald-100 text-emerald-700',
  'Kapatıldı': 'bg-red-100 text-red-700',
};

export default function StaffDashboard() {
  const [tickets, setTickets] = useState([]);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [slaStatus, setSlaStatus] = useState([]);
  const [recurring, setRecurring] = useState([]);

  useEffect(() => {
    Promise.all([
      api.get('/tickets'),
      api.get('/users'),
      api.get('/sla/status').catch(() => ({ data: [] })),
      api.get('/sla/recurring').catch(() => ({ data: [] }))
    ]).then(([ticketRes, userRes, slaRes, recRes]) => {
      setTickets(ticketRes.data);
      setUsers(userRes.data);
      setSlaStatus(slaRes.data);
      setRecurring(recRes.data);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const stats = {
    total: tickets.length,
    open: tickets.filter(t => !['Çözüldü', 'Kapatıldı'].includes(t.Status)).length,
    newTickets: tickets.filter(t => t.Status === 'Yeni').length,
    solved: tickets.filter(t => t.Status === 'Çözüldü').length,
    unassigned: tickets.filter(t => !t.AssignedTo && !['Çözüldü', 'Kapatıldı'].includes(t.Status)).length,
    customers: users.filter(u => u.Role === 'customer').length,
  };

  const activeTickets = tickets.filter(t => !['Çözüldü', 'Kapatıldı', 'Logo\'ya İletildi'].includes(t.Status));
  const closedTickets = tickets.filter(t => ['Çözüldü', 'Kapatıldı'].includes(t.Status));
  const unassignedActive = activeTickets.filter(t => !t.AssignedTo);
  const assignedActive = activeTickets.filter(t => t.AssignedTo);

  // Personel bazlı iş ayrıştırma
  const staffWorkload = {};
  activeTickets.forEach(t => {
    if (t.AssignedTo && t.AssignedToName) {
      if (!staffWorkload[t.AssignedTo]) staffWorkload[t.AssignedTo] = { name: t.AssignedToName, tickets: [] };
      staffWorkload[t.AssignedTo].tickets.push(t);
    }
  });

  const staffColors = ['bg-blue-500', 'bg-purple-500', 'bg-emerald-500', 'bg-amber-500', 'bg-rose-500', 'bg-cyan-500', 'bg-indigo-500', 'bg-teal-500'];

  // Group by status for mini chart
  const statusGroups = {};
  tickets.forEach(t => { statusGroups[t.Status] = (statusGroups[t.Status] || 0) + 1; });

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-slate-800">Dashboard</h1>
        <Link to="/staff/tickets" className="text-sm text-blue-600 hover:underline font-medium flex items-center gap-1">
          Ticket Board <ArrowRight size={14} />
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        {[
          { label: 'Toplam Ticket', value: stats.total, icon: Ticket, gradient: 'from-blue-500 to-blue-600' },
          { label: 'Açık', value: stats.open, icon: Clock, gradient: 'from-amber-500 to-orange-500' },
          { label: 'Yeni', value: stats.newTickets, icon: AlertCircle, gradient: 'from-violet-500 to-purple-600' },
          { label: 'Atanmamış', value: stats.unassigned, icon: TrendingUp, gradient: 'from-rose-500 to-red-600' },
          { label: 'Çözülen', value: stats.solved, icon: CheckCircle, gradient: 'from-emerald-500 to-green-600' },
          { label: 'Müşteri', value: stats.customers, icon: Users, gradient: 'from-cyan-500 to-teal-600' },
        ].map((stat, i) => (
          <div key={i} className="bg-white rounded-xl p-4 shadow-sm border border-slate-100 hover:shadow-md transition-shadow">
            <div className={`w-10 h-10 bg-gradient-to-br ${stat.gradient} rounded-xl flex items-center justify-center mb-3`}>
              <stat.icon size={20} className="text-white" />
            </div>
            <p className="text-2xl font-bold text-slate-800">{stat.value}</p>
            <p className="text-xs text-slate-500 mt-0.5">{stat.label}</p>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Aktif Bekleyen Talepler */}
        <div className="lg:col-span-2 bg-white rounded-xl shadow-sm border border-slate-100">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="text-base font-semibold text-slate-800">Aktif Bekleyen Talepler</h2>
            <Link to="/staff/tickets" className="text-xs text-blue-600 hover:underline font-medium">Tümü</Link>
          </div>
          <div className="divide-y divide-slate-100">
            {unassignedActive.length === 0 && (
              <div className="px-5 py-8 text-center text-slate-400 text-sm">Atanmamış bekleyen talep yok</div>
            )}
            {unassignedActive.slice(0, 8).map(ticket => (
              <Link key={ticket.Id} to={`/staff/tickets/${ticket.Id}`} className="flex items-center justify-between px-5 py-3 hover:bg-slate-50 transition-colors group">
                <div className="flex-1 min-w-0 mr-3">
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-mono text-slate-400">#{ticket.Id}</span>
                    <h3 className="text-sm font-medium text-slate-800 truncate">{ticket.Title}</h3>
                  </div>
                  <div className="flex items-center gap-2 mt-1 flex-wrap">
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColors[ticket.Status] || 'bg-slate-100'}`}>{ticket.Status}</span>
                    {ticket.Tags && ticket.Tags.map(tag => (
                      <span key={tag.Id} className="text-[10px] px-1.5 py-0.5 rounded-full font-medium" style={{ backgroundColor: tag.Color + '20', color: tag.Color }}>{tag.Name}</span>
                    ))}
                    <span className="text-xs text-slate-400">{ticket.ContactName || ticket.CreatedByName}{ticket.CustomerName ? ` • ${ticket.CustomerName}` : ''}</span>
                    {ticket.AssignedToName && <span className="text-xs font-semibold text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded-full">→ {ticket.AssignedToName}</span>}
                  </div>
                </div>
                <span className="text-xs text-slate-400">{new Date(ticket.CreatedAt).toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' })}</span>
              </Link>
            ))}
          </div>
        </div>

        {/* Status Distribution */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <h2 className="text-base font-semibold text-slate-800 mb-4">Durum Dağılımı</h2>
          <div className="space-y-3">
            {Object.entries(statusGroups).map(([status, count]) => (
              <div key={status}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium text-slate-600">{status}</span>
                  <span className="text-xs font-bold text-slate-800">{count}</span>
                </div>
                <div className="w-full bg-slate-100 rounded-full h-2">
                  <div className={`h-2 rounded-full ${status === 'Yeni' ? 'bg-blue-500' : status === 'Çözüldü' ? 'bg-emerald-500' : status === 'Kapatıldı' ? 'bg-red-500' : 'bg-amber-500'}`}
                    style={{ width: `${(count / tickets.length) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Personel Üzerindeki Aktif İşler */}
      {Object.keys(staffWorkload).length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-slate-100">
          <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
            <h2 className="text-base font-semibold text-slate-800">Personel Üzerindeki Aktif İşler</h2>
            <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full">{assignedActive.length} talep • {Object.keys(staffWorkload).length} personel</span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 p-5">
            {Object.entries(staffWorkload).map(([userId, data], idx) => (
              <div key={userId} className="rounded-xl border border-slate-200 overflow-hidden">
                <div className={`${staffColors[idx % staffColors.length]} px-4 py-2.5 flex items-center justify-between`}>
                  <div className="flex items-center gap-2">
                    <div className="w-7 h-7 bg-white/20 rounded-full flex items-center justify-center text-white text-xs font-bold">{data.name.charAt(0)}</div>
                    <span className="text-white font-semibold text-sm">{data.name}</span>
                  </div>
                  <span className="text-white/80 text-xs font-bold bg-white/20 px-2 py-0.5 rounded-full">{data.tickets.length} iş</span>
                </div>
                <div className="divide-y divide-slate-100">
                  {data.tickets.slice(0, 5).map(t => (
                    <Link key={t.Id} to={`/staff/tickets/${t.Id}`} className="flex items-center justify-between px-3 py-2 hover:bg-slate-50 transition-colors">
                      <div className="flex items-center gap-2 min-w-0 flex-1">
                        <span className="text-xs font-mono text-slate-400">#{t.Id}</span>
                        <span className="text-xs text-slate-700 truncate">{t.Title}</span>
                      </div>
                      <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors[t.Status] || 'bg-slate-100'}`}>{t.Status}</span>
                    </Link>
                  ))}
                  {data.tickets.length > 5 && <p className="text-[10px] text-slate-400 text-center py-1.5">+{data.tickets.length - 5} daha</p>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Second Row - SLA, Satisfaction, Recurring */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {/* SLA Alerts */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <Shield size={18} className="text-red-500" />
            <h2 className="text-base font-semibold text-slate-800">SLA Durumu</h2>
          </div>
          {(() => {
            const breached = slaStatus.filter(t => t.SLABreached || (t.ResolutionMinutes && t.ElapsedMinutes > t.ResolutionMinutes));
            const atRisk = slaStatus.filter(t => !t.SLABreached && t.ResolutionMinutes && t.ElapsedMinutes > t.ResolutionMinutes * 0.8 && t.ElapsedMinutes <= t.ResolutionMinutes);
            return (
              <div className="space-y-3">
                <div className="flex items-center justify-between p-3 bg-red-50 rounded-xl">
                  <span className="text-sm text-red-700 font-medium">SLA Aşılmış</span>
                  <span className="text-lg font-bold text-red-700">{breached.length}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-amber-50 rounded-xl">
                  <span className="text-sm text-amber-700 font-medium">Risk Altında</span>
                  <span className="text-lg font-bold text-amber-700">{atRisk.length}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-emerald-50 rounded-xl">
                  <span className="text-sm text-emerald-700 font-medium">Normal</span>
                  <span className="text-lg font-bold text-emerald-700">{slaStatus.length - breached.length - atRisk.length}</span>
                </div>
                <Link to="/staff/sla" className="block text-center text-xs text-blue-600 hover:underline font-medium mt-2">
                  Detaylı SLA Raporu →
                </Link>
              </div>
            );
          })()}
        </div>

        {/* Recurring Tickets */}
        <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center gap-2 mb-4">
            <RefreshCw size={18} className="text-purple-500" />
            <h2 className="text-base font-semibold text-slate-800">Tekrarlayan Talepler</h2>
          </div>
          {recurring.length > 0 ? (
            <div className="space-y-2.5">
              {recurring.slice(0, 5).map((r, i) => (
                <div key={i} className="flex items-center justify-between p-2.5 bg-purple-50 rounded-lg">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs font-medium text-slate-700 truncate">{r.ContactName || r.ContactEmail}</p>
                    <p className="text-xs text-slate-500">{r.Category}</p>
                  </div>
                  <span className="text-xs font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded-full">{r.TicketCount}x</span>
                </div>
              ))}
              {recurring.length === 0 && <p className="text-xs text-slate-400 text-center py-4">Tekrarlayan talep yok</p>}
            </div>
          ) : (
            <p className="text-sm text-slate-400 text-center py-6">Son 30 günde tekrarlayan talep yok</p>
          )}
        </div>
      </div>

      {/* Çözülmüş/Kapatılmış Talepler */}
      {closedTickets.length > 0 && (
        <details className="bg-white rounded-xl shadow-sm border border-slate-100">
          <summary className="px-5 py-4 cursor-pointer text-base font-semibold text-slate-800 hover:bg-slate-50 rounded-xl flex items-center justify-between">
            <span>Çözülmüş / Kapatılmış Talepler</span>
            <span className="text-xs font-medium text-slate-400 bg-slate-100 px-2.5 py-1 rounded-full">{closedTickets.length} kayıt</span>
          </summary>
          <div className="divide-y divide-slate-100 border-t border-slate-100">
            {closedTickets.slice(0, 10).map(ticket => (
              <Link key={ticket.Id} to={`/staff/tickets/${ticket.Id}`} className="flex items-center justify-between px-5 py-2.5 hover:bg-slate-50 transition-colors">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <span className="text-xs font-mono text-slate-400">#{ticket.Id}</span>
                  <span className="text-sm text-slate-600 truncate">{ticket.Title}</span>
                  <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${statusColors[ticket.Status]}`}>{ticket.Status}</span>
                  <span className="text-xs text-slate-400">{ticket.ContactName || ticket.CreatedByName}</span>
                </div>
                <span className="text-xs text-slate-400">{new Date(ticket.ClosedAt || ticket.UpdatedAt).toLocaleDateString('tr-TR', { timeZone: 'Europe/Istanbul' })}</span>
              </Link>
            ))}
            {closedTickets.length > 10 && (
              <div className="px-5 py-2 text-center">
                <Link to="/staff/tickets" className="text-xs text-blue-600 hover:underline">Tüm kayıtları gör →</Link>
              </div>
            )}
          </div>
        </details>
      )}

      {/* First Response Time Widget */}
      <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
        <div className="flex items-center gap-2 mb-4">
          <Timer size={18} className="text-blue-500" />
          <h2 className="text-base font-semibold text-slate-800">İlk Yanıt Süresi Performansı</h2>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {(() => {
            const responded = tickets.filter(t => t.FirstResponseAt && t.CreatedAt);
            const avgMinutes = responded.length > 0 
              ? responded.reduce((sum, t) => sum + (new Date(t.FirstResponseAt) - new Date(t.CreatedAt)) / 60000, 0) / responded.length 
              : 0;
            const withinSLA = responded.filter(t => {
              const respMins = (new Date(t.FirstResponseAt) - new Date(t.CreatedAt)) / 60000;
              return respMins <= 240; // 4 saat default
            }).length;
            const noResponse = tickets.filter(t => !t.FirstResponseAt && !['Çözüldü', 'Kapatıldı'].includes(t.Status)).length;
            
            return (
              <>
                <div className="text-center p-3 bg-blue-50 rounded-xl">
                  <p className="text-xl font-bold text-blue-700">{avgMinutes > 60 ? `${Math.round(avgMinutes / 60)}sa` : `${Math.round(avgMinutes)}dk`}</p>
                  <p className="text-xs text-blue-600">Ort. İlk Yanıt</p>
                </div>
                <div className="text-center p-3 bg-emerald-50 rounded-xl">
                  <p className="text-xl font-bold text-emerald-700">{responded.length}</p>
                  <p className="text-xs text-emerald-600">Yanıtlanan</p>
                </div>
                <div className="text-center p-3 bg-amber-50 rounded-xl">
                  <p className="text-xl font-bold text-amber-700">{noResponse}</p>
                  <p className="text-xs text-amber-600">Yanıt Bekleyen</p>
                </div>
                <div className="text-center p-3 bg-purple-50 rounded-xl">
                  <p className="text-xl font-bold text-purple-700">{responded.length > 0 ? Math.round((withinSLA / responded.length) * 100) : 0}%</p>
                  <p className="text-xs text-purple-600">SLA İçinde</p>
                </div>
              </>
            );
          })()}
        </div>
      </div>
    </div>
  );
}
